import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';

const MIN_THUMB = 40;

interface Sizes {
  /** Thumb width in px. */
  thumb: number;
  /** How far the thumb can travel along the track, px. */
  travel: number;
  /** How far the grid can scroll, px. */
  range: number;
}

/**
 * The schedule's horizontal scrollbar, drawn between the ‹ › buttons instead of
 * under the grid (where it sat off-screen on long schedules). Drawn rather than
 * native so it looks and behaves the same on Windows and on macOS, which hides
 * native scrollbars until you scroll. The grid still scrolls natively — trackpad,
 * Shift+wheel and touch move it, and this follows; dragging the thumb or
 * clicking the track moves the grid.
 *
 * Scrolling never goes through React: the thumb is positioned straight on the
 * element, and both following the grid and dragging are coalesced to one update
 * per frame. React only re-renders when the sizes change.
 */
export function ScheduleScrollTrack({
  scrollerRef,
  onPage,
}: {
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  /** Moves the grid one page left (-1) or right (1). */
  onPage: (direction: -1 | 1) => void;
}): React.ReactElement {
  const trackRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const [sizes, setSizes] = useState<Sizes>({ thumb: 0, travel: 0, range: 0 });
  const sizesRef = useRef(sizes);
  const [dragging, setDragging] = useState(false);
  const drag = useRef<{ x: number; left: number; pointerX: number } | null>(null);
  const frame = useRef(0);

  /** Places the thumb for the grid's current scroll position. */
  const paint = useCallback(() => {
    const el = scrollerRef.current;
    const thumb = thumbRef.current;
    if (!el || !thumb) return;
    const { travel, range } = sizesRef.current;
    const x = range > 0 ? (el.scrollLeft / range) * travel : 0;
    thumb.style.transform = `translate3d(${x}px, 0, 0)`;
  }, [scrollerRef]);

  /** One paint per frame, however many scroll events arrive. */
  const schedule = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      const d = drag.current;
      const el = scrollerRef.current;
      if (d && el) {
        const { travel, range } = sizesRef.current;
        if (travel > 0) el.scrollLeft = d.left + ((d.pointerX - d.x) * range) / travel;
      }
      paint();
    });
  }, [paint, scrollerRef]);

  useEffect(() => {
    const el = scrollerRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const measure = (): void => {
      const width = track.clientWidth;
      const range = Math.max(0, el.scrollWidth - el.clientWidth);
      const thumb =
        el.scrollWidth > 0
          ? Math.min(width, Math.max(MIN_THUMB, (width * el.clientWidth) / el.scrollWidth))
          : width;
      const next = { thumb, travel: Math.max(0, width - thumb), range };
      const prev = sizesRef.current;
      if (prev.thumb !== next.thumb || prev.travel !== next.travel || prev.range !== next.range) {
        sizesRef.current = next;
        setSizes(next);
      }
      paint();
    };
    const first = requestAnimationFrame(measure);
    el.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    observer.observe(track);
    const table = el.querySelector('table');
    if (table) observer.observe(table);
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(frame.current);
      frame.current = 0;
      el.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, [scrollerRef, schedule, paint]);

  const onThumbDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const el = scrollerRef.current;
    if (!el) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, left: el.scrollLeft, pointerX: event.clientX };
    setDragging(true);
  };
  const onThumbMove = (event: React.PointerEvent<HTMLDivElement>): void => {
    if (!drag.current) return;
    drag.current.pointerX = event.clientX;
    schedule();
  };
  const onThumbUp = (): void => {
    drag.current = null;
    setDragging(false);
  };

  // A click on the bare track pages towards it, as a native scrollbar does.
  const onTrackDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return;
    const x = event.clientX - track.getBoundingClientRect().left;
    const thumbLeft = thumb.getBoundingClientRect().left - track.getBoundingClientRect().left;
    onPage(x < thumbLeft ? -1 : 1);
  };

  return (
    // The visible track is 6px; the row around it is the easy-to-hit target.
    <div
      ref={trackRef}
      role="scrollbar"
      aria-orientation="horizontal"
      aria-label="Scroll the schedule across days"
      onPointerDown={onTrackDown}
      className="group relative flex h-5 min-w-0 flex-1 cursor-pointer items-center"
    >
      <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-700/60" aria-hidden />
      <div
        ref={thumbRef}
        onPointerDown={onThumbDown}
        onPointerMove={onThumbMove}
        onPointerUp={onThumbUp}
        onPointerCancel={onThumbUp}
        className="absolute inset-y-0 left-0 flex cursor-grab touch-none items-center will-change-transform"
        style={{ width: sizes.thumb }}
      >
        <div
          className={cn(
            'h-1.5 w-full rounded-full bg-gray-400 transition-colors group-hover:bg-gray-500 dark:bg-gray-500 dark:group-hover:bg-gray-400',
            dragging && 'cursor-grabbing bg-gray-500 dark:bg-gray-400'
          )}
        />
      </div>
    </div>
  );
}
